import React, {
    useEffect,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
    getBannersEnabled,
    getBannersPlaceholderId,
} from '~spa/Utils/SiteConfig';
import {
    getBrowsingCountryIso,
    getCurrencyIso,
    getCustomerId,
    getLanguageIso,
    getUserMode,
    getCountryState,
    getIsFtdWrapperFlag,
    getIsHybridWrapperFlag,
    getIsMobileWrapperFlag,
    getIsPromotionPermittedWrapperFlag,
    getIsPromotionRestrictedWrapperFlag,
    getIsVipWrapperFlag,
    getRmsWrapperData,
    getSerialWrapperData,
    onIsFtdFlagChange,
    onIsPromotionPermittedFlagChange,
    onIsPromotionRestrictedFlagChange,
    onIsVipFlagChange,
    onRmsDataChange,
    onSerialDataChange,
} from '~shared/utils/user';
import { getState } from '~shared/utils/state';

import './styles.scss';

const BannersComponent = ({ placeholderId }) => {
    const bannersErrorEventName = 'bannerBuildSliderErrorEvent';

    // config
    const languageCode = getLanguageIso();
    const deviceCode = getIsMobileWrapperFlag() ? 'mobile' : 'desktop';

    const [isVisible, setIsVisible] = useState(true);
    const hideBanners = () => setIsVisible(false);

    // segmentation
    const [vip, setVip] = useState(getIsVipWrapperFlag());
    const [ftd, setFtd] = useState(getIsFtdWrapperFlag());
    const countries = getBrowsingCountryIso();
    const currency = getCurrencyIso();
    const countryState = getCountryState();
    const userMode = getUserMode();
    const [rms, setRms] = useState(getRmsWrapperData());
    const [serials, setSerials] = useState(getSerialWrapperData() || '');
    const cid = getCustomerId() || '';
    const isHybrid = getIsHybridWrapperFlag();
    const [isPromotionPermitted, setIsPromotionPermitted] = useState(
        getIsPromotionPermittedWrapperFlag()
    );
    const [isPromotionRestricted, setIsPromotionRestricted] = useState(
        getIsPromotionRestrictedWrapperFlag()
    );

    useEffect(() => {
        document.addEventListener(bannersErrorEventName, hideBanners);
        const clientStateSubscribers = [];

        clientStateSubscribers.push(
            onIsFtdFlagChange(
                (key, isFtd) => setFtd(isFtd)
            )
        );

        clientStateSubscribers.push(
            onIsPromotionPermittedFlagChange(
                (key, isPromotionPermitted) => setIsPromotionPermitted(isPromotionPermitted)
            )
        );

        clientStateSubscribers.push(
            onIsPromotionRestrictedFlagChange(
                (key, isPromotionRestricted) => setIsPromotionRestricted(isPromotionRestricted)
            )
        );

        clientStateSubscribers.push(
            onIsVipFlagChange(
                (key, isVip) => setVip(isVip)
            )
        );

        clientStateSubscribers.push(
            onRmsDataChange(
                (key, rms) => setRms(rms)
            )
        );

        clientStateSubscribers.push(
            onSerialDataChange(
                (key, serial) => setSerials(serial)
            )
        );

        return function cleanup() {
            document.removeEventListener(bannersErrorEventName, hideBanners);

            clientStateSubscribers.forEach(
                (unsubscribe) => unsubscribe()
            );
        };
    }, []);

    if (!isVisible) {
        return null;
    }

    const config = {
        placeholderId,
        deviceCode,
        languageCode,
    };
    const segmentation = {
        vip,
        ftd,
        countries,
        currency,
        userMode,
        serials,
        cid,
        isHybrid,
        isPromotionPermitted,
        isPromotionRestricted,
        states: countryState,
        riskFactor: rms, // Important!! riskFactor is a wrong field name in banners component!
    };

    const classes = classNames(
        'hpBanners',
        `hpBanners--${deviceCode}`,
    );

    return (
        <div className={classes}>
            <banner-888
                config={JSON.stringify(config)}
                segmentation={JSON.stringify(segmentation)}
            />
        </div>
    );
};

BannersComponent.propTypes = {
    placeholderId: PropTypes.string.isRequired,
};

const Banners = () => {
    const bannersEnabled = getBannersEnabled();
    const placeholderId = getBannersPlaceholderId();

    if (!bannersEnabled || !placeholderId) {
        return null;
    }

    return <BannersComponent placeholderId={placeholderId} />;
};

/**
 * Get a custom banner for Cheltenham 2022
 *
 * To be removed after.
 * Hardcoded here to facilitate removal and not to export the main BannersComponent.
 * @see https://dedsert.atlassian.net/browse/AM-3877 (implementation)
 * @see https://dedsert.atlassian.net/browse/AM-4108 (removal)
 * @returns {JSX} Custom banner
 */
const BannersCheltenham2022 = () => {
    const bannersEnabled = getBannersEnabled();
    const placeholderId = getState('banners:placeholderId:cheltenham2022');

    if (!bannersEnabled || !placeholderId) {

        return null;
    }

    return <BannersComponent placeholderId={placeholderId} />;
};

export { Banners, BannersCheltenham2022 };
